import React, {useRef} from 'react';
import {nanoid} from 'nanoid';
import {
	Checkbox,
	type CheckboxProps,
	ListItem,
	ListItemAvatar,
	ListItemButton,
	listItemButtonClasses,
	type ListItemButtonProps,
	type ListItemProps,
	ListItemText,
} from '@mui/material';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {styled} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils';

const CheckboxListItemRoot = styled(ListItem)(({theme}) => ({
	// need to make more room for the switch
	[`& > .${listItemButtonClasses.root}`]: {
		paddingRight: 70,
	},
}));

export interface CheckboxListItemProps extends CheckboxProps {
	avatar?: React.ReactNode;
	description?: string;
	label: string;
	ListItemProps?: Partial<Omit<ListItemProps, 'secondaryAction'>>;
	ListItemButtonProps?: Partial<ListItemButtonProps>;
}

export const CheckboxListItemPrimitive = (
	{
		classes,
		description,
		inputRef,
		label,
		ListItemProps,
		ListItemButtonProps,
		...props
	}: CheckboxListItemProps,
	ref: any,
) => {
	const sharedInputRef = useSharedRef(null, [inputRef]);
	const uuid = useRef(nanoid());

	return (
		<CheckboxListItemRoot
			ref={ref}
			disablePadding
			{...ListItemProps}
			secondaryAction={
				<Checkbox
					{...props}
					edge="end"
					inputProps={{
						'aria-labelledby': `${uuid.current}-label`,
						...props.inputProps,
					}}
					inputRef={sharedInputRef}
					tabIndex={-1}
					onClick={(event) => {
						// prevent the click event from bubbling up to the list item
						event.stopPropagation();
						props.onClick?.(event);
					}}
				/>
			}
		>
			<ListItemButton
				aria-disabled={props.disabled}
				aria-pressed={props.checked ?? props.value === true}
				aria-checked={props.checked ?? props.value === true}
				disableRipple
				{...ListItemButtonProps}
				onClick={splitEventHandler(ListItemButtonProps?.onClick, () => {
					// pipe the click event to the input
					(sharedInputRef.current as unknown as HTMLElement)?.click();
				})}
				style={{
					cursor: props.disabled ? 'not-allowed' : undefined,
					...ListItemButtonProps?.style,
				}}
			>
				<ListItemText
					id={`${uuid.current}-label`}
					primary={label}
					secondary={description}
				/>
			</ListItemButton>
		</CheckboxListItemRoot>
	);
};

/**
 * Combines the functionality of a ListItem with a Checkbox.
 */
export const CheckboxListItem = React.forwardRef(CheckboxListItemPrimitive) as (
	props: CheckboxListItemProps,
) => JSX.Element;

export default CheckboxListItem;
